"use client";

import { useAuth } from "@/hooks/use-auth";
import { useUserStore } from "@/stores/user-store";
import { setUserPreferences } from "@/utils/user-preferences";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Detect relationship from URL pattern
 * - /sell, /sell/*, /dashboard/seller/* → seller
 * - /buy, /buy/*, /dashboard/buyer/* → buyer
 */
export function getRelationshipFromUrl(pathname: string): "buyer" | "seller" | null {
	if (pathname.startsWith("/sell") || pathname.startsWith("/dashboard/seller")) {
		return "seller";
	}
	if (pathname.startsWith("/buy") || pathname.startsWith("/dashboard/buyer")) {
		return "buyer";
	}
	return null;
}

/**
 * Two-Stage Membership, Relationship & Group Auto-Correction Flow
 *
 * SCOPE: Seller pages (/sell?rawUnitId=...&groupId=..., /dashboard/seller/review/:id)
 *
 * STAGE 1 - On Page Load (Automatic):
 *   ✅ Auto-switch ONLY if value doesn't exist at all
 *   ❌ Do NOT auto-switch if value exists (even if wrong)
 *
 *   Rules:
 *   - If no relationship exists → assign from URL pattern
 *   - If no groupId exists → assign from memberGroupId
 *
 * STAGE 2 - On User Action (Deferred):
 *   ✅ Force correct values on form submit/save
 *   ✅ Override incorrect values
 *
 *   Use `ensureCorrectGroupForAction()` callback during form submission.
 *
 * WHY:
 *   - On page load, we don't know if user intends to act or just opened the page
 *   - Avoid breaking other active flows (e.g., buyer pages in another tab)
 *   - Only correct on explicit user action
 *
 * @param isMember - Whether the user is a member
 * @param memberGroupId - The group ID from membership validation
 */
export function useGroupSwitch({ isMember, memberGroupId }: { isMember: boolean; memberGroupId?: string }) {
	const { user } = useAuth();
	const pathname = usePathname();
	const selectedGroupId = useUserStore(state => state.selectedGroupId);
	const relationship = useUserStore(state => state.relationship);
	const setSelectedGroup = useUserStore(state => state.setSelectedGroup);

	// Track what we've already saved to prevent duplicate saves
	const hasAutoSwitchedRelationshipRef = useRef(false);
	const hasAutoSwitchedGroupIdRef = useRef(false);

	// Detect relationship from URL (e.g., /sell/*, /dashboard/seller/*)
	const urlRelationship = getRelationshipFromUrl(pathname);

	// The group in the user's account that matches the target
	const targetGroup =
		isMember && memberGroupId && user?.groups ? (user.groups.find(g => g.id === memberGroupId) ?? null) : null;

	// User's account contains the target group — guard can be bypassed
	const canSwitch = Boolean(targetGroup);

	// Relationship from the target group (buyer | seller)
	const targetRelationship = targetGroup?.relationship as "buyer" | "seller" | undefined;

	// Effective values to use (priority: targetGroup > URL)
	const effectiveRelationship = targetRelationship || urlRelationship;
	const effectiveGroupId = memberGroupId;

	// ═══════════════════════════════════════════════════════════════════════════
	// STAGE 1: Automatic switch on page load
	// ONLY if value doesn't exist at all (not if it's wrong)
	// ═══════════════════════════════════════════════════════════════════════════

	// Rule: If no relationship exists → add valid relationship
	useEffect(() => {
		if (hasAutoSwitchedRelationshipRef.current) return;
		if (relationship) return; // Already has relationship, don't auto-switch (even if wrong)
		if (!effectiveRelationship) return; // Nothing to set

		hasAutoSwitchedRelationshipRef.current = true;
		setSelectedGroup({ relationship: effectiveRelationship });
	}, [relationship, effectiveRelationship, setSelectedGroup]);

	// Rule: If no groupId exists → add valid groupId
	useEffect(() => {
		if (hasAutoSwitchedGroupIdRef.current) return;
		if (selectedGroupId) return; // Already has groupId, don't auto-switch (even if wrong)
		if (!effectiveGroupId) return; // Nothing to set

		hasAutoSwitchedGroupIdRef.current = true;
		setSelectedGroup({ selectedGroupId: effectiveGroupId });
	}, [selectedGroupId, effectiveGroupId, setSelectedGroup]);

	// ═══════════════════════════════════════════════════════════════════════════
	// STAGE 2: Deferred switch on user action
	// Call this during form submission to force correct values
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Returns the correct groupId and relationship to use for mutations.
	 * Also updates the store/IndexedDB for future renders.
	 *
	 * IMPORTANT: Always use the returned values for mutations, not the store values,
	 * because the store update won't be reflected until the next render.
	 */
	const ensureCorrectGroupForAction = useCallback(() => {
		const needsRelationshipFix = effectiveRelationship && relationship !== effectiveRelationship;
		const needsGroupIdFix = effectiveGroupId && selectedGroupId !== effectiveGroupId;

		// Update store for future renders (but these won't be available until next render)
		if (needsRelationshipFix || needsGroupIdFix) {
			const updates: { relationship?: "buyer" | "seller"; selectedGroupId?: string } = {};

			if (needsRelationshipFix) {
				updates.relationship = effectiveRelationship;
			}
			if (needsGroupIdFix) {
				updates.selectedGroupId = effectiveGroupId;
			}

			setSelectedGroup(updates);
			setUserPreferences(updates);
			toast.info(
				`Switched to valid ${needsRelationshipFix ? "relationship" : ""} ${needsGroupIdFix && needsRelationshipFix ? "and" : ""} ${needsGroupIdFix ? "group" : ""}`,
			);
		}
	}, [effectiveRelationship, effectiveGroupId, relationship, selectedGroupId, setSelectedGroup]);

	return {
		canSwitch,
		// Call this during form submission to ensure correct group/relationship
		ensureCorrectGroupForAction,
		// For checking current state
		isRelationshipCorrect: !effectiveRelationship || relationship === effectiveRelationship,
		isGroupIdCorrect: !effectiveGroupId || selectedGroupId === effectiveGroupId,
		// Effective values (for reference)
		effectiveRelationship,
		effectiveGroupId,
	};
}
